// Pricing cards rendered from the live /plans feed. Shared by the
// landing pricing section and the /pricing page. Copy comes from the
// `landing.pricing` / `landing.featureLabels` dictionary.

import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { fmt, useDict } from "@/i18n/LocaleProvider";
import type { BillingPeriod, PlanRow } from "@/hooks/usePlans";
import { useAuthStore } from "@/stores/authStore";

export function PlanCards({
  plans,
  period = "monthly",
}: {
  plans: PlanRow[];
  period?: BillingPeriod;
}) {
  const navigate = useNavigate();
  const { landing } = useDict();
  const t = landing.pricing;
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const limit = (n: number) => (n === 0 ? t.unlimited : n.toString());

  // Paid self-serve plans go to the in-app checkout; logged-out visitors
  // register first, then auto-resume checkout. Enterprise is sales.
  function handleBuy(slug: string) {
    if (slug === "free") {
      navigate(isAuthed ? "/app" : "/login?mode=register");
      return;
    }
    if (slug === "enterprise") {
      window.open(
        "mailto:sales@truehacking.ai?subject=" +
          encodeURIComponent("Enterprise plan"),
        "_blank",
      );
      return;
    }
    const dest = `/app/upgrade?plan=${slug}&autostart=1`;
    navigate(
      isAuthed ? dest : `/login?mode=register&next=${encodeURIComponent(dest)}`,
    );
  }

  if (plans.length === 0) {
    return (
      <div className="text-center font-mono text-[12px] text-dim py-12">
        {t.loading}
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {plans.map((p) => {
        const isPopular = p.slug === "pro";
        const paid = p.price_cents > 0 && p.slug !== "enterprise";
        const showAnnual = period === "annual" && paid;
        const priceDisplay =
          p.slug === "enterprise"
            ? t.custom
            : showAnnual
              ? (p.prices?.[p.currency]?.annual.display ?? p.price_display)
              : p.price_display;
        const cadence = showAnnual ? t.perYear : t.perMonth;
        return (
          <div
            key={p.slug}
            className="p-6 rounded flex flex-col"
            style={{
              background: "rgb(var(--color-bg2))",
              border: isPopular
                ? "2px solid rgb(var(--color-accent))"
                : "1px solid rgb(var(--color-stroke))",
              position: "relative",
            }}
          >
            {isPopular && (
              <div
                className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-[3px] rounded font-mono text-[9px] font-bold uppercase tracking-[0.1em]"
                style={{ background: "rgb(var(--color-accent))", color: "#000" }}
              >
                {t.mostPopular}
              </div>
            )}
            <div className="font-display text-[20px] font-bold text-content mb-1">
              {p.name}
            </div>
            <div className="font-mono text-[10px] text-muted mb-4 leading-[1.5] min-h-[36px]">
              {p.description}
            </div>
            <div className="mb-4">
              <div className="font-display text-[32px] font-bold text-accent leading-none">
                {priceDisplay}
              </div>
              {paid && (
                <div className="font-mono text-[10px] text-dim mt-1">
                  {cadence}
                </div>
              )}
            </div>
            <ul className="font-mono text-[11px] text-muted mb-5 flex-1 space-y-[6px]">
              <li>
                <span className="text-success mr-1">✓</span>
                {fmt(t.engagements, { n: limit(p.max_engagements) })}
              </li>
              <li>
                <span className="text-success mr-1">✓</span>
                {fmt(t.scans, { n: limit(p.max_scans_per_month) })}
              </li>
              <li>
                <span className="text-success mr-1">✓</span>
                {fmt(t.users, { n: limit(p.max_users) })}
              </li>
              {Object.entries(p.features)
                .filter(([, v]) => v)
                .map(([k]) => (
                  <li key={k}>
                    <span className="text-success mr-1">✓</span>
                    {landing.featureLabels[
                      k as keyof typeof landing.featureLabels
                    ] ?? k.replace(/_/g, " ")}
                  </li>
                ))}
            </ul>
            <Button
              variant={isPopular ? "primary" : "default"}
              full
              onClick={() => handleBuy(p.slug)}
            >
              {p.slug === "free"
                ? t.ctaFree
                : p.slug === "enterprise"
                  ? t.ctaEnterprise
                  : fmt(t.ctaGet, { name: p.name })}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
